import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  User,
  Calendar,
  DollarSign,
  MoreVertical,
  Archive,
  ArchiveRestore,
  ShieldCheck,
  Flag,
  FlagOff,
  Loader2,
} from "lucide-react";
import { jobsApi, techniciansApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

const COLUMNS = [
  { key: "Unscheduled", label: "Unscheduled", statuses: ["Unassigned"], primaryStatus: "Unassigned" },
  { key: "Scheduled", label: "Scheduled", statuses: ["Scheduled"], primaryStatus: "Scheduled" },
  { key: "Dispatched", label: "Dispatched", statuses: ["Dispatched"], primaryStatus: "Dispatched" },
  { key: "In Progress", label: "In Progress", statuses: ["In Progress", "En Route", "On Site"], primaryStatus: "In Progress" },
  { key: "Follow-Up Required", label: "Follow-Up Required", statuses: [], primaryStatus: null },
  { key: "Completed", label: "Completed", statuses: ["Completed"], primaryStatus: "Completed" },
  { key: "Canceled", label: "Canceled", statuses: ["Canceled"], primaryStatus: "Canceled" },
];

const STATUS_OPTIONS = ["Unassigned", "Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled"];

const BOARD_VIEW_OPTIONS = [
  { value: "active", label: "Active Jobs" },
  { value: "active_recent", label: "Active + Recently Completed" },
  { value: "archived", label: "Archived Jobs" },
  { value: "all", label: "All Jobs" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const FINANCIAL_LABEL_STYLE = {
  "Completed-Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Payment Due": "bg-amber-50 text-amber-700 border-amber-200",
  "Invoice Not Sent": "bg-slate-100 text-slate-600 border-slate-200",
};

function columnForJob(job) {
  if (job.followUpRequired) return "Follow-Up Required";
  return COLUMNS.find((c) => c.statuses.includes(job.status))?.key || "Unscheduled";
}

export default function JobsBoardView() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [technicians, setTechnicians] = useState([]);
  const [movingId, setMovingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const drag = useRef(null);

  const [view, setView] = useState("active_recent");
  const [techId, setTechId] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    techniciansApi.list().then(setTechnicians).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { view };
      if (techId) params.techId = techId;
      if (status) params.status = status;
      if (paymentStatus) params.paymentStatus = paymentStatus;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (search) params.q = search;
      setJobs(await jobsApi.board(params));
    } catch (err) {
      toast.error("Failed to load job board");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, techId, status, paymentStatus, dateFrom, dateTo, search]);

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("job-status-changed", refresh);
    window.addEventListener("pipeline-refresh", refresh);
    return () => {
      window.removeEventListener("job-status-changed", refresh);
      window.removeEventListener("pipeline-refresh", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, techId, status, paymentStatus, dateFrom, dateTo, search]);

  const cardsByColumn = useMemo(() => {
    const m = {};
    COLUMNS.forEach((c) => (m[c.key] = []));
    jobs.forEach((j) => {
      const key = columnForJob(j);
      (m[key] = m[key] || []).push(j);
    });
    return m;
  }, [jobs]);

  const moveCard = async (job, column) => {
    if (!column.primaryStatus && column.key !== "Follow-Up Required") return;
    setMovingId(job.id);
    try {
      if (column.key === "Follow-Up Required") {
        if (!job.needsFollowUp) await jobsApi.setFollowUp(job.id, true);
      } else {
        if (job.needsFollowUp) await jobsApi.setFollowUp(job.id, false);
        if (job.status !== column.primaryStatus) await jobsApi.updateStatus(job.id, column.primaryStatus);
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not move card");
    } finally {
      setMovingId(null);
    }
  };

  const doAction = async (job, action) => {
    try {
      if (action === "archive") await jobsApi.archiveNow(job.id);
      else if (action === "restore") await jobsApi.restoreToBoard(job.id);
      else if (action === "keep") await jobsApi.keepOnBoard(job.id);
      else if (action === "follow_up_on") await jobsApi.setFollowUp(job.id, true);
      else if (action === "follow_up_off") await jobsApi.setFollowUp(job.id, false);
      toast.success("Updated");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div data-testid="jobs-board-view">
      <FilterBar
        view={view} setView={setView}
        techId={techId} setTechId={setTechId}
        technicians={technicians}
        status={status} setStatus={setStatus}
        paymentStatus={paymentStatus} setPaymentStatus={setPaymentStatus}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        searchInput={searchInput} setSearchInput={setSearchInput}
      />

      {loading ? (
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="px-6 pb-6 overflow-x-auto" data-testid="jobs-board-columns">
          <div className="flex gap-3 items-start min-h-[60vh]" style={{ minWidth: "min-content" }}>
            {COLUMNS.map((col) => {
              const colCards = cardsByColumn[col.key] || [];
              return (
                <div
                  key={col.key}
                  data-testid={`jobs-board-column-${col.key.replace(/\s+/g, "-")}`}
                  className={`w-[280px] shrink-0 rounded-xl border transition-colors ${
                    overColumn === col.key ? "border-blue-400 bg-blue-50/40" : "border-slate-200 bg-slate-50/60"
                  }`}
                  onDragOver={(e) => {
                    if (drag.current) {
                      e.preventDefault();
                      setOverColumn(col.key);
                    }
                  }}
                  onDragLeave={() => setOverColumn((c) => (c === col.key ? null : c))}
                  onDrop={() => {
                    if (drag.current) {
                      moveCard(drag.current, col);
                      setOverColumn(null);
                      drag.current = null;
                    }
                  }}
                >
                  <div className="px-2.5 py-2.5 flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-slate-800 truncate" data-testid={`jobs-board-column-name-${col.key.replace(/\s+/g, "-")}`}>
                      {col.label}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-400 shrink-0">{colCards.length}</span>
                  </div>
                  <div className="px-2 pb-2 space-y-2 min-h-[120px]">
                    {colCards.map((job) => (
                      <JobBoardCard
                        key={job.id}
                        job={job}
                        moving={movingId === job.id}
                        onOpen={() => navigate(`/jobs?id=${job.id}`)}
                        onDragStart={() => {
                          drag.current = job;
                        }}
                        onDragEnd={() => {
                          drag.current = null;
                          setOverColumn(null);
                        }}
                        onAction={(action) => doAction(job, action)}
                      />
                    ))}
                    {colCards.length === 0 && (
                      <div className="text-[11.5px] text-slate-300 text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
                        Drop here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterBar({
  view, setView, techId, setTechId, technicians, status, setStatus,
  paymentStatus, setPaymentStatus, dateFrom, setDateFrom, dateTo, setDateTo,
  searchInput, setSearchInput,
}) {
  return (
    <div className="px-6 pt-4 pb-3 flex flex-wrap items-center gap-2" data-testid="jobs-board-filter-bar">
      <select
        value={view}
        onChange={(e) => setView(e.target.value)}
        data-testid="jobs-board-filter-view"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] font-medium text-slate-700 bg-white"
      >
        {BOARD_VIEW_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
        data-testid="jobs-board-filter-date-from"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] text-slate-700 bg-white"
      />
      <span className="text-[12px] text-slate-400">to</span>
      <input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
        data-testid="jobs-board-filter-date-to"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] text-slate-700 bg-white"
      />

      <select
        value={techId}
        onChange={(e) => setTechId(e.target.value)}
        data-testid="jobs-board-filter-tech"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] font-medium text-slate-700 bg-white"
      >
        <option value="">All Technicians</option>
        {technicians.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>

      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        data-testid="jobs-board-filter-status"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] font-medium text-slate-700 bg-white"
      >
        <option value="">All Statuses</option>
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      <select
        value={paymentStatus}
        onChange={(e) => setPaymentStatus(e.target.value)}
        data-testid="jobs-board-filter-payment-status"
        className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] font-medium text-slate-700 bg-white"
      >
        <option value="">Any Payment Status</option>
        <option value="Completed-Paid">Completed — Paid</option>
        <option value="Payment Due">Payment Due</option>
        <option value="Invoice Not Sent">Invoice Not Sent</option>
      </select>

      <div className="relative">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search customer, title, job #, address"
          data-testid="jobs-board-filter-search"
          className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-[12.5px] text-slate-700 bg-white w-64"
        />
      </div>
    </div>
  );
}

function JobBoardCard({ job, moving, onOpen, onDragStart, onDragEnd, onAction }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const archived = job.boardVisibility === "archived";

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const isTerminal = job.status === "Completed" || job.status === "Canceled";

  return (
    <div
      draggable={!moving && !archived}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`jobs-board-card-${job.id}`}
      className={`relative rounded-lg border bg-white p-2.5 shadow-sm transition-all ${
        archived ? "border-slate-200 opacity-70" : "border-slate-200 hover:border-blue-300 hover:shadow-md cursor-grab active:cursor-grabbing"
      } ${moving ? "opacity-50" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-slate-900 truncate">{job.title || job.type || "Job"}</div>
          <div className="text-[11px] text-slate-600 truncate">{job.customer}</div>
          <div className="text-[10.5px] text-slate-500 truncate flex items-center gap-1">
            <User className="h-2.5 w-2.5" /> {job.tech || "Unassigned"}
          </div>
          <div className="text-[10px] text-slate-400 truncate">{job.id}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="inline-flex items-center gap-0.5 text-[12px] font-bold text-emerald-600">
            <DollarSign className="h-3 w-3" />
            {money(job.value)}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              data-testid={`jobs-board-card-menu-${job.id}`}
              className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
            {menuOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                data-testid={`jobs-board-card-menu-panel-${job.id}`}
                className="absolute right-0 mt-1 w-52 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden z-40"
              >
                {archived ? (
                  <MenuItem icon={ArchiveRestore} label="Restore to Board" testid={`jobs-board-action-restore-${job.id}`}
                    onClick={() => { onAction("restore"); setMenuOpen(false); }} />
                ) : (
                  <>
                    {job.needsFollowUp ? (
                      <MenuItem icon={FlagOff} label="Clear Follow-Up Required" testid={`jobs-board-action-followup-off-${job.id}`}
                        onClick={() => { onAction("follow_up_off"); setMenuOpen(false); }} />
                    ) : (
                      <MenuItem icon={Flag} label="Mark Follow-Up Required" testid={`jobs-board-action-followup-on-${job.id}`}
                        onClick={() => { onAction("follow_up_on"); setMenuOpen(false); }} />
                    )}
                    {isTerminal && !job.archiveHold && (
                      <MenuItem icon={ShieldCheck} label="Keep on Board" testid={`jobs-board-action-keep-${job.id}`}
                        onClick={() => { onAction("keep"); setMenuOpen(false); }} />
                    )}
                    <MenuItem icon={Archive} label={isTerminal ? "Archive Now" : "Remove from Board Now"} testid={`jobs-board-action-archive-${job.id}`}
                      danger onClick={() => { onAction("archive"); setMenuOpen(false); }} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {job.start && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500">
            <Calendar className="h-3 w-3" /> {fmtDate(job.start)}
          </span>
        )}
        {job.financialStatus && (
          <span
            data-testid={`jobs-board-label-financial-${job.id}`}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${FINANCIAL_LABEL_STYLE[job.financialStatus] || FINANCIAL_LABEL_STYLE["Invoice Not Sent"]}`}
          >
            {job.financialStatus === "Completed-Paid" ? "Completed — Paid" : job.financialStatus}
          </span>
        )}
        {job.signatureMissing && (
          <span data-testid={`jobs-board-label-signature-${job.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
            Signature Missing
          </span>
        )}
        {job.followUpRequired && (
          <span data-testid={`jobs-board-label-followup-${job.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200">
            Follow-Up Required
          </span>
        )}
        {job.archiveHold && (
          <span data-testid={`jobs-board-label-hold-${job.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">
            Archive Hold
          </span>
        )}
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full px-3 py-2 text-left text-[13px] flex items-center gap-2 hover:bg-slate-50 ${
        danger ? "text-rose-600" : "text-slate-700"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}
